import {
  Alert,
  Anchor,
  Badge,
  Card,
  Center,
  Group,
  Loader,
  SimpleGrid,
  Stack,
  Text,
  Title,
} from '@mantine/core';
import type { ReactNode } from 'react';
import { Link } from 'react-router';
import { ErrorMessage } from '../components/ErrorMessage.js';
import { OfflineQueueNotice } from '../components/OfflineQueueNotice.js';
import { useT } from '../i18n/useT.js';
import type { MessageKey } from '../i18n/da.js';
import { useDashboard } from '../api/hooks.js';
import type { DashboardCounts } from '../api/hooks.js';

/**
 * Overblik — what needs attention.
 *
 * Built last of the thirteen pages although the task list puts it third. Each
 * widget is a view onto a feature whose page is built in 10.2 and 10.4, so
 * building it earlier would have meant building each one against a page that
 * did not exist yet.
 *
 * **The numbers come from `GET /v1/dashboard`, not from counting list results.**
 * No list endpoint returns a row count -- they return at most `limit` rows --
 * so a widget counting what it received would say "200" for a company with nine
 * hundred outstanding documents, and keep saying 200 as the backlog was worked
 * through. See docs/reference/dashboard.md.
 *
 * Every count links to the page that resolves it. A number nobody can act on is
 * decoration.
 *
 * **No capture buttons.** "Tag billede" and "Log tur" sat above the counts until
 * they moved to the installed app and Indstillinger → Mobil-app (see
 * CaptureActions). This is a desk screen about what is outstanding; two
 * full-width phone buttons at the top pushed the counts off the first screen.
 */

interface Widget {
  readonly count: number;
  /*
    Keys rather than text. The widget list is data, and building it from
    translated strings would freeze it in whichever language rendered first --
    the list is rebuilt on every render, but a `useMemo` over it would not be.
  */
  readonly label: MessageKey;
  readonly explanation: MessageKey;
  readonly to: string;
  readonly action: MessageKey;
}

function widgetsFor(counts: DashboardCounts): readonly Widget[] {
  return [
    {
      count: counts.unlinkedAttachments,
      label: 'dash.unlinked.label',
      explanation: 'dash.unlinked.explanation',
      to: '/bilag',
      action: 'dash.unlinked.action',
    },
    {
      count: counts.undocumentedEntries,
      /*
        Deliberately next to the card above, and worded to separate them. The
        two are opposite backlogs and read as the same one at a glance: that is
        documents waiting for a posting, this is posteringer waiting for a
        document. Only this one is a defect in the books — bogføringsloven
        requires every registrering to be documented — so it says so.
      */
      label: 'dash.undocumented.label',
      explanation: 'dash.undocumented.explanation',
      to: '/kladde',
      action: 'dash.undocumented.action',
    },
    {
      count: counts.draftEntries,
      /*
        Next to the two above because it is the third kind of unfinished
        work, and the easiest of the three to forget: a kladde is left out of
        every report on purpose, so nothing else will ever mention it.
      */
      label: 'dash.drafts.label',
      explanation: 'dash.drafts.explanation',
      to: '/kladde',
      action: 'dash.drafts.action',
    },
    {
      count: counts.unmatchedBankTransactions,
      label: 'dash.unmatched.label',
      explanation: 'dash.unmatched.explanation',
      to: '/bank',
      action: 'dash.unmatched.action',
    },
    {
      count: counts.partiallyMatchedBankTransactions,
      /*
        Counted and shown separately, never folded into the line above or into
        "afstemt". These are matched and still carry a difference: partly
        accounted for, and still needing a person. A dashboard that hid them
        would report the bank as done while a linje was half-settled.
      */
      label: 'dash.partial.label',
      explanation: 'dash.partial.explanation',
      to: '/bank',
      action: 'dash.partial.action',
    },
  ];
}

function CountCard({ widget }: { readonly widget: Widget }): ReactNode {
  const t = useT();
  const needsAttention = widget.count > 0;

  return (
    <Card withBorder padding="md">
      <Stack gap="xs" h="100%" justify="space-between">
        <Stack gap="xs">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Text fw={500}>{t(widget.label)}</Text>
            <Badge
              size="lg"
              variant={needsAttention ? 'filled' : 'light'}
              color={needsAttention ? 'orange' : 'gray'}
            >
              {widget.count}
            </Badge>
          </Group>
          <Text size="sm" c="dimmed">
            {t(widget.explanation)}
          </Text>
        </Stack>

        {/* Every count leads somewhere. A number nobody can act on is decoration. */}
        <Anchor component={Link} to={widget.to} size="sm">
          {t(widget.action)}
        </Anchor>
      </Stack>
    </Card>
  );
}

/** `2026-01-01` as `01.01.2026`, without constructing a Date from it. */
function danishDate(iso: string): string {
  const [year, month, day] = iso.split('-');
  return `${day ?? ''}.${month ?? ''}.${year ?? ''}`;
}

function VatCard({ counts }: { readonly counts: DashboardCounts }): ReactNode {
  const t = useT();
  const period = counts.oldestOpenVatPeriod;

  return (
    <Card withBorder padding="md">
      <Stack gap="xs" h="100%" justify="space-between">
        <Stack gap="xs">
          <Group justify="space-between" align="flex-start" wrap="nowrap">
            <Text fw={500}>{t('dash.vat.title')}</Text>
            <Badge size="lg" variant="light" color="gray">
              {counts.openVatPeriods}
            </Badge>
          </Group>

          {period === null ? (
            <Text size="sm" c="dimmed">
              {t('dash.vat.none')}
            </Text>
          ) : (
            <Stack gap={4}>
              <Text size="sm">{t('dash.vat.first')}</Text>
              {/* One text node: a date range is one piece of information. */}
              <Text size="sm" fw={500}>
                {`${danishDate(period.periodStart)}–${danishDate(period.periodEnd)}`}
              </Text>
              {/*
                Grey, not orange. An open period is the normal state of a
                company mid-quarter -- it becomes a task at its deadline, not
                because it exists. A warning that is always on says nothing.
              */}
              <Text size="sm" c="dimmed">
                {t('dash.vat.notATask')}
              </Text>
            </Stack>
          )}
        </Stack>

        <Anchor component={Link} to="/moms" size="sm">
          {t('dash.vat.action')}
        </Anchor>
      </Stack>
    </Card>
  );
}

export function DashboardPage(): ReactNode {
  const t = useT();
  const dashboard = useDashboard();

  return (
    <Stack gap="lg">
      <Title order={2}>{t('dash.title')}</Title>

      <OfflineQueueNotice />

      {dashboard.isPending && (
        <Center p="xl">
          <Loader />
        </Center>
      )}

      {dashboard.isError && (
        <ErrorMessage error={dashboard.error} title={t('dash.loadFailed')} />
      )}

      {dashboard.data !== undefined && (
        <>
          {dashboard.data.clear ? (
            <Alert color="green" variant="light" title={t('dash.clearTitle')}>
              {t('dash.clear')}
            </Alert>
          ) : (
            <Alert color="orange" variant="light" title={t('dash.outstandingTitle')}>
              {t('dash.outstanding')}
            </Alert>
          )}

          <SimpleGrid cols={{ base: 1, sm: 2, lg: 3 }} spacing="md">
            {widgetsFor(dashboard.data).map((widget) => (
              <CountCard key={widget.label} widget={widget} />
            ))}
            <VatCard counts={dashboard.data} />
          </SimpleGrid>

          <Text size="sm" c="dimmed">
            {t('dash.scope')}
          </Text>
        </>
      )}
    </Stack>
  );
}
